import React, { useEffect, useState, useRef, useMemo } from 'react';
import { FileSpreadsheet, Search, Loader2, List, ArrowUpDown } from 'lucide-react';
import * as API from '../../../api';
import { PageTitle } from '../../../../_metronic/layout/core';
import { StatsCard } from '../StatsCard';
import { SearchInput } from '../SearchInput';
import { ListTable } from './ListTable';
import * as XLSX from 'xlsx';
import Select from 'react-select';

type Props = {
  className?: string;
};

const ComparacaoBeneficioValor: React.FC<Props> = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<any>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [allData, setAllData] = useState<any>([]);
  const [displayData, setDisplayData] = useState<any>([]);
  const [columns, setColumns] = useState<any>([]);
  const [sortConfig, setSortConfig] = useState<{ key: number; direction: 'asc' | 'desc' } | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<{ value: string; label: string } | null>(null);

  const limit = 500;

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    const filteredData = filterData(allData);
    setDisplayData(filteredData.slice(0, limit));
  }, [searchTerm, allData, sortConfig, selectedMonth]);

  // Get unique months from data
  const monthOptions = useMemo(() => {
    if (!allData.length) return [];
    
    const months = new Set(allData.map((item: any) => item[5])); // MesPedido is at index 5
    return Array.from(months)
      .filter(Boolean)
      .sort()
      .map(month => ({
        value: month as string,
        label: month as string
      }));
  }, [allData]);

  const filterData = (data: any[]) => {
    let filteredData = [...data];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredData = filteredData.filter((item: any) => 
        Object.values(item).some((value: any) => 
          value?.toString().toLowerCase().includes(searchLower)
        )
      );
    }

    // Apply month filter
    if (selectedMonth) {
      filteredData = filteredData.filter(item => item[5] === selectedMonth.value);
    }

    // Apply sorting
    if (sortConfig) {
      filteredData.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === bValue) return 0;
        
        // Handle numeric values
        if (!isNaN(aValue) && !isNaN(bValue)) {
          return sortConfig.direction === 'asc' 
            ? Number(aValue) - Number(bValue)
            : Number(bValue) - Number(aValue);
        }
        
        // Handle string values
        return sortConfig.direction === 'asc'
          ? aValue > bValue ? 1 : -1
          : aValue < bValue ? 1 : -1;
      });
    }

    return filteredData;
  };

  const getData = async () => {
    setIsLoading(true);
    try {
      const response = await API.getComparacaoBeneficioValorxValorCupomUtilizado();
      setData(response.data);
      setColumns(response.columns);
      setAllData(response.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (columnIndex: number) => {
    setSortConfig(current => {
      if (!current || current.key !== columnIndex) {
        return { key: columnIndex, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key: columnIndex, direction: 'desc' };
      }
      return null;
    });
  };

  const exportToXLSX = () => {
    if (!displayData.length) return;

    const dataForSheet = displayData.map((item: any) => ({
      'ID Pedido': item[0],
      'ID Regra Promoção': item[1],
      'Cupom': item[2],
      'Beneficio Valor': item[3],
      'Data Pedido': item[4],
      'Mês Pedido': item[5],
      'Valor Utilizado Cupom': item[6],
      'Comparação': item[7],
      'Status Comparação': item[8]
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataForSheet);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Validação Comparação Beneficio");
    XLSX.writeFile(workbook, "validacao_comparacao_beneficio_valor.xlsx");
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <PageTitle>Validação - Comparação Beneficio Valor x Valor Cupom Utilizado</PageTitle>
            <button
              onClick={exportToXLSX}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FileSpreadsheet className="w-5 h-5 mr-2" />
              Exportar Planilha
            </button>
          </div>

          {!isLoading && (
            <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <StatsCard
                title="Total de Resultados"
                value={allData.length}
                description={allData.length > limit ? `Mostrando ${limit} de ${allData.length} registros` : 'Registros encontrados'}
                icon={<List className="w-6 h-6" />}
              />
              {searchTerm && (
                <StatsCard
                  title="Resultados da Busca"
                  value={displayData.length}
                  description="Registros encontrados com o termo pesquisado"
                  icon={<Search className="w-6 h-6" />}
                />
              )}
              {selectedMonth && (
                <StatsCard
                  title="Filtro por Mês"
                  value={displayData.length}
                  description={`Registros de ${selectedMonth.label}`}
                  icon={<ArrowUpDown className="w-6 h-6" />}
                />
              )}
            </div>
          )}
        </div>

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar em todos os campos..."
          />
          <Select
            isClearable
            placeholder="Filtrar por Mês"
            options={monthOptions}
            value={selectedMonth}
            onChange={setSelectedMonth}
            className="w-full sm:w-64"
          />
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <p className="mt-4 text-gray-500">Carregando dados...</p>
            </div>
          ) : !displayData.length ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="bg-gray-100 rounded-full p-3">
                <List className="w-8 h-8 text-gray-400" />
              </div>
              <p className="mt-4 text-gray-500">Nenhum registro encontrado</p>
            </div>
          ) : (
            <ListTable
              data={displayData}
              columns={columns}
              rule={[{col:8, value:'ATENÇÃO', func:'='}]}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ComparacaoBeneficioValor;